import { useEffect, useState } from "react";
import { api, formatApiError, uploadFile, mediaUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Check, ExternalLink, UserPlus, ShieldCheck, Languages, MessageCircle } from "lucide-react";
import { LANGS } from "@/lib/langs";

export function WorkspaceSettings({ open, onOpenChange, workspace, setWorkspace, user }) {
  const isAdmin = user?.role === "business_admin";
  const [form, setForm] = useState({ display_name: "", brand_color: "#2ac67b", welcome_message: "", tagline: "", widget_avatar: "", calls_enabled: true, auto_assign: true, translation_enabled: false, agent_language: "hi-IN", pre_chat_enabled: false, pre_chat_name: true, pre_chat_phone: false, pre_chat_email: false, pre_chat_name_required: false, pre_chat_phone_required: false, pre_chat_email_required: false, identity_verification: false, whatsapp_redirect_enabled: false, whatsapp_number: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState({ label: "", color: "#3b82f6" });
  const [savingTags, setSavingTags] = useState(false);
  const [agents, setAgents] = useState([]);
  const [newAgent, setNewAgent] = useState({ name: "", email: "", password: "" });
  const [addingAgent, setAddingAgent] = useState(false);
  const [copied, setCopied] = useState("");

  const origin = window.location.origin;
  const embedSnippet = `<script src="${origin}/embed.js" data-superchat-key="${workspace?.api_key}" async></script>`;
  const directUrl = `${origin}/widget/${workspace?.api_key}`;

  useEffect(() => {
    if (workspace) {
      setForm({
        display_name: workspace.display_name || workspace.name || "",
        brand_color: workspace.brand_color || "#2ac67b",
        welcome_message: workspace.welcome_message || "",
        tagline: workspace.tagline || "",
        widget_avatar: workspace.widget_avatar || "",
        calls_enabled: workspace.calls_enabled !== false,
        auto_assign: workspace.auto_assign !== false,
        translation_enabled: !!workspace.translation_enabled,
        agent_language: workspace.agent_language || "hi-IN",
        pre_chat_enabled: !!workspace.pre_chat_enabled,
        pre_chat_name: workspace.pre_chat_name !== false,
        pre_chat_phone: !!workspace.pre_chat_phone,
        pre_chat_email: !!workspace.pre_chat_email,
        pre_chat_name_required: !!workspace.pre_chat_name_required,
        pre_chat_phone_required: !!workspace.pre_chat_phone_required,
        pre_chat_email_required: !!workspace.pre_chat_email_required,
        identity_verification: !!workspace.identity_verification,
        whatsapp_redirect_enabled: !!workspace.whatsapp_redirect_enabled,
        whatsapp_number: workspace.whatsapp_number || "",
      });
    }
  }, [workspace]);

  useEffect(() => {
    if (workspace?.conversation_tags) setTags(workspace.conversation_tags);
  }, [workspace]);

  const saveTags = async (list) => {
    setSavingTags(true);
    try {
      const { data } = await api.put("/workspace/tags", { tags: list });
      setTags(data.conversation_tags);
      setWorkspace({ ...workspace, conversation_tags: data.conversation_tags });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingTags(false);
    }
  };
  const addTag = () => {
    const label = newTag.label.trim();
    if (!label) { toast.error("Enter a tag name"); return; }
    if (tags.some((t) => t.label.toLowerCase() === label.toLowerCase())) { toast.error("Tag already exists"); return; }
    const list = [...tags, { label, color: newTag.color }];
    setNewTag({ label: "", color: "#3b82f6" });
    saveTags(list);
  };
  const removeTag = (label) => saveTags(tags.filter((t) => t.label !== label));

  const rotateSecret = async () => {
    try {
      const { data } = await api.post("/workspace/rotate-identity-secret");
      setWorkspace({ ...workspace, identity_secret: data.identity_secret });
      toast.success("Identity secret rotated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (open && isAdmin) loadAgents();
  }, [open, isAdmin]);

  const loadAgents = async () => {
    try {
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch {}
  };

  const saveWidget = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/workspace", form);
      setWorkspace(data);
      toast.success("Widget settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploadingAvatar(true);
    try {
      const info = await uploadFile(file, "avatar");
      const next = { ...form, widget_avatar: info.file_url };
      setForm(next);
      // Persist immediately so the widget picks it up without a separate save.
      const { data } = await api.patch("/workspace", next);
      setWorkspace(data);
      toast.success("Profile image updated");
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    const next = { ...form, widget_avatar: "" };
    setForm(next);
    try {
      const { data } = await api.patch("/workspace", next);
      setWorkspace(data);
      toast.success("Profile image removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addAgent = async () => {
    if (!newAgent.name || !newAgent.email || !newAgent.password) {
      toast.error("Fill all agent fields");
      return;
    }
    setAddingAgent(true);
    try {
      await api.post("/agents", newAgent);
      toast.success("Agent added");
      setNewAgent({ name: "", email: "", password: "" });
      loadAgents();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddingAgent(false);
    }
  };

  const removeAgent = async (id) => {
    try {
      await api.delete(`/agents/${id}`);
      toast.success("Agent removed");
      loadAgents();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const Toggle = ({ checked, onChange, label, disabled }) => (
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${disabled ? "opacity-40" : "hover:bg-secondary/50"}`} data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="truncate">{label}</span>
      <span className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors ${checked ? "bg-[#2ac67b]" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </span>
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))", paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }} data-testid="workspace-settings-sheet">
        <SheetHeader>
          <SheetTitle className="text-xl">Workspace settings</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="widget" className="mt-6">
          <TabsList className="w-full">
            <TabsTrigger value="widget" className="flex-1" data-testid="tab-widget">Widget</TabsTrigger>
            {isAdmin && <TabsTrigger value="tags" className="flex-1" data-testid="tab-tags">Tags</TabsTrigger>}
            {isAdmin && <TabsTrigger value="agents" className="flex-1" data-testid="tab-agents">Agents</TabsTrigger>}
            <TabsTrigger value="install" className="flex-1" data-testid="tab-install">Install</TabsTrigger>
          </TabsList>

          <TabsContent value="widget" className="mt-5 space-y-4">
            <div>
              <Label>Profile image</Label>
              <p className="mb-2 text-xs text-muted-foreground">Shown in the widget header. Square image works best.</p>
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border bg-secondary/40 text-lg font-semibold text-muted-foreground">
                  {form.widget_avatar
                    ? <img src={mediaUrl(form.widget_avatar)} alt="Profile" className="h-full w-full object-cover" data-testid="settings-avatar-preview" />
                    : (form.display_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className={`cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary/50 ${uploadingAvatar ? "pointer-events-none opacity-60" : ""}`} data-testid="settings-avatar-upload-label">
                    {uploadingAvatar ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.widget_avatar ? "Replace image" : "Upload image")}
                    <input type="file" accept="image/*" hidden onChange={handleAvatar} disabled={!isAdmin || uploadingAvatar} data-testid="settings-avatar-input" />
                  </label>
                  {form.widget_avatar && (
                    <button type="button" onClick={removeAvatar} disabled={!isAdmin} className="rounded-lg px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10" data-testid="settings-avatar-remove">Remove</button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label>Display name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="settings-display-name" />
            </div>
            <div>
              <Label>Brand color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border" data-testid="settings-brand-color" />
                <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="flex-1" />
              </div>
            </div>
            <div>
              <Label>Welcome message</Label>
              <Textarea value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} rows={2} data-testid="settings-welcome" />
            </div>
            <div>
              <Label>Status tagline</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="settings-tagline" />
            </div>

            <Toggle checked={form.calls_enabled} onChange={(v) => setForm({ ...form, calls_enabled: v })} label="Voice calls in widget" />
            <Toggle checked={form.auto_assign} onChange={(v) => setForm({ ...form, auto_assign: v })} label="Auto-assign new chats to the least-busy online agent" />

            {isAdmin && (
              <div className="space-y-3 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <MessageCircle className="h-4 w-4 text-[#25D366]" />
                    WhatsApp redirect
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">When enabled, clicking the website launcher opens WhatsApp instead of SuperChat.</p>
                </div>
                <Toggle
                  checked={form.whatsapp_redirect_enabled}
                  onChange={(v) => setForm({ ...form, whatsapp_redirect_enabled: v })}
                  label="Redirect widget to WhatsApp"
                />
                <div>
                  <Label className="text-xs">WhatsApp number</Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={form.whatsapp_number}
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    placeholder="+91 98765 43210"
                    data-testid="settings-whatsapp-number"
                  />
                  <p className="mt-1.5 text-[11px] text-muted-foreground">Include the country code. Indian 10-digit numbers automatically receive +91.</p>
                </div>
              </div>
            )}

            <div className="space-y-2 rounded-xl border p-3">
              <Toggle checked={form.translation_enabled} onChange={(v) => setForm({ ...form, translation_enabled: v })} label="Live translation" />
              {form.translation_enabled && (
                <div className="pt-1">
                  <p className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] uppercase tracking-wide text-muted-foreground"><Languages className="h-3 w-3" /> Agents read & write in</p>
                  <select
                    value={form.agent_language}
                    onChange={(e) => setForm({ ...form, agent_language: e.target.value })}
                    className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
                    data-testid="settings-agent-language"
                  >
                    {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                  <p className="mt-1.5 px-1 text-[11px] text-muted-foreground">Customers write in their own language (auto-detected); each side sees messages translated for them. Uses Sarvam AI — bills per message/voice.</p>
                </div>
              )}
            </div>

            <div className="space-y-2 rounded-xl border p-3">
              <Toggle checked={form.pre_chat_enabled} onChange={(v) => setForm({ ...form, pre_chat_enabled: v })} label="Pre-chat form (capture leads)" />
              {form.pre_chat_enabled && (
                <div className="space-y-2 pt-1">
                  <p className="px-1 text-[11px] uppercase tracking-wide text-muted-foreground">Fields · toggle required</p>
                  {[
                    { key: "name", label: "Name" },
                    { key: "phone", label: "Phone number" },
                    { key: "email", label: "Email address" },
                  ].map((f) => (
                    <div key={f.key} className="grid grid-cols-2 gap-2">
                      <Toggle checked={form[`pre_chat_${f.key}`]} onChange={(v) => setForm({ ...form, [`pre_chat_${f.key}`]: v })} label={f.label} />
                      <Toggle checked={form[`pre_chat_${f.key}_required`]} onChange={(v) => setForm({ ...form, [`pre_chat_${f.key}_required`]: v })} label="Required" disabled={!form[`pre_chat_${f.key}`]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isAdmin ? (
              <Button onClick={saveWidget} disabled={saving} className="w-full bg-[#2ac67b] hover:bg-[#22c55e]" data-testid="save-widget-settings">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Only workspace admins can edit these settings.</p>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="tags" className="mt-5 space-y-4">
              <p className="text-sm text-muted-foreground">Tags your team can apply to conversations (e.g. VIP, Fake Deposit, Casino Problem). Manage the list here; agents pick from it in each chat.</p>
              <div className="rounded-xl border bg-secondary/40 p-4">
                <p className="mb-3 text-sm font-semibold">Add a tag</p>
                <div className="flex items-center gap-2">
                  <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="h-10 w-12 flex-shrink-0 cursor-pointer rounded-md border" data-testid="new-tag-color" />
                  <Input placeholder="Tag name" value={newTag.label} onChange={(e) => setNewTag({ ...newTag, label: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addTag()} data-testid="new-tag-label" />
                  <Button onClick={addTag} disabled={savingTags} className="flex-shrink-0 bg-[#2ac67b] hover:bg-[#22c55e]" data-testid="add-tag-save">
                    {savingTags ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="tag-palette-list">
                {tags.length === 0 && <p className="text-sm text-muted-foreground">No tags yet.</p>}
                {tags.map((t) => (
                  <span key={t.label} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium" style={{ background: `${t.color}22`, color: t.color, border: `1px solid ${t.color}55` }} data-testid={`palette-tag-${t.label}`}>
                    {t.label}
                    <button onClick={() => removeTag(t.label)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${t.label}`} data-testid={`palette-remove-${t.label}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="agents" className="mt-5 space-y-4">
              <div className="rounded-xl border bg-secondary/40 p-4">
                <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4" /> Add agent</p>
                <div className="space-y-2">
                  <Input placeholder="Full name" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} data-testid="new-agent-name" />
                  <Input placeholder="Email" type="email" value={newAgent.email} onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })} data-testid="new-agent-email" />
                  <Input placeholder="Password" type="text" value={newAgent.password} onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })} data-testid="new-agent-password" />
                  <Button onClick={addAgent} disabled={addingAgent} className="w-full bg-[#2ac67b] hover:bg-[#22c55e]" data-testid="add-agent-button">
                    {addingAgent ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add agent</>}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {agents.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-lg border px-3 py-2" data-testid={`agent-row-${a.id}`}>
                    <div>
                      <div className="text-sm font-medium">{a.name} {a.role === "business_admin" && <span className="ml-1 rounded bg-[#2ac67b]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#2ac67b]">ADMIN</span>}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </div>
                    {a.role === "agent" && (
                      <button onClick={() => removeAgent(a.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-agent-${a.id}`} aria-label="Remove agent">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          <TabsContent value="install" className="mt-5 space-y-4">
            <div>
              <Label>Embed on your website</Label>
              <p className="mb-2 text-xs text-muted-foreground">Paste this before the closing &lt;/body&gt; tag.</p>
              <div className="relative rounded-lg bg-[#0a1512] p-3 pr-10 text-xs text-[#9fe7d8]">
                <code className="break-all">{embedSnippet}</code>
                <button onClick={() => copy(embedSnippet, "embed")} className="absolute right-2 top-2 text-white/60 hover:text-white" data-testid="copy-embed-code">
                  {copied === "embed" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Widget API key</Label>
              <div className="relative rounded-lg border bg-secondary/40 p-3 pr-10 text-xs">
                <code className="break-all">{workspace?.api_key}</code>
                <button onClick={() => copy(workspace?.api_key, "key")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground" data-testid="copy-api-key">
                  {copied === "key" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <a href={directUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-lg border border-[#2ac67b]/30 py-2.5 text-sm font-medium text-[#2ac67b] hover:bg-[#2ac67b]/5" data-testid="preview-widget-link">
              <ExternalLink className="h-4 w-4" /> Preview widget
            </a>

            {isAdmin && (
              <div className="space-y-3 rounded-xl border p-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4" /> Pass logged-in users automatically</p>
                  <p className="mt-1 text-xs text-muted-foreground">Call <code className="rounded bg-secondary px-1">SuperChat.identify(&#123;name, phone, userId&#125;)</code> from your app to open chats pre-filled with the signed-in user — no pre-chat form.</p>
                </div>
                <Toggle checked={form.identity_verification} onChange={(v) => setForm({ ...form, identity_verification: v })} label="Require identity verification (HMAC)" />
                {form.identity_verification && (
                  <p className="text-[11px] text-muted-foreground">When on, sessions with a <code>userId</code> must include <code>userHash = HMAC_SHA256(secret, userId)</code> computed on your server. Prevents users impersonating others.</p>
                )}
                <div>
                  <Label className="text-xs">Identity secret (keep server-side)</Label>
                  <div className="relative rounded-lg border bg-secondary/40 p-3 pr-10 text-xs">
                    <code className="break-all">{workspace?.identity_secret || "—"}</code>
                    <button onClick={() => copy(workspace?.identity_secret, "secret")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground" data-testid="copy-identity-secret">
                      {copied === "secret" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <button onClick={rotateSecret} className="mt-2 text-xs text-red-500 hover:underline" data-testid="rotate-identity-secret">Rotate secret</button>
                </div>
                {isAdmin && (
                  <Button onClick={saveWidget} disabled={saving} variant="outline" className="w-full" data-testid="save-identity-settings">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save identity settings"}
                  </Button>
                )}
              </div>
            )}

            <a href="/docs" target="_blank" rel="noreferrer" className="block text-center text-xs text-muted-foreground hover:underline">
              View full integration & REST API docs →
            </a>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
